import React, { useRef, useEffect, useState } from 'react';
import { IoInformationCircleOutline } from 'react-icons/io5';
import PunchPointsModal from './PunchPointsModal';
import PropTypes from 'prop-types';
import { Form, Button, Row, Col, Table, Modal, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { SUBMIT_FORM, VIEW_FORM } from 'actions/formActions';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { cookie } from 'utils/cookies';

const CanopyForm = ({ role = cookie.get('user-role'), userId = cookie.get('user-id') }) => {
  const dispatch = useDispatch();
  const { viewFormData } = useSelector((state) => state.checklist);
  const { formId, stationId } = useParams();
  const navigate = useNavigate();

  const sigCanvas = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [showPunchModal, setShowPunchModal] = useState(false);
  const [pendingRework, setPendingRework] = useState(null);
  const [currentSignature, setCurrentSignature] = useState({ role: '' });
  const [isLoading, setIsLoading] = useState(true);
  const dataLoadedRef = useRef(false);

  const { register, control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      stationId: stationId || '',
      station_id: stationId || '',
      project_name: '',
      inspection_date: '',
      vendor_name: '',
      canopy_size: '',
      canopy_serial_number: '',
      equipment_tag: '',

      visual_checks: Array(32).fill({ status: '', remark: '' }),
      physical_checks: Array(7).fill({ status: '', remark: '' }),
      corrective_actions: Array(5).fill({ issue: '', action: '', target_date: '' }),

      canopy_found_okay: '',
      summary_remarks: '',
      other_comments: '',

      comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      status: 'draft',
      manager_approve: false,
      punch_points: '',
    },
  });

  const formValues = useWatch({ control });
  const punch_points = useWatch({ control, name: 'punch_points' });
  const statusWatch = useWatch({ control, name: 'status' });
  const manager_id = useWatch({ control, name: 'manager_id' });

  // ---------- Autosave (single-level) ----------
  const autoSave = React.useCallback(
    _.debounce((data) => {
      if (!dataLoadedRef.current) return;
      const payload = {
        form_data: {
          status: data.status,
          form_id: formId,
          formName: 'canopy',
          station_id: data.station_id || data.stationId,
          stationId: data.stationId || data.station_id,
          project_name: data.project_name,
          inspection_date: data.inspection_date,
          vendor_name: data.vendor_name,
          canopy_size: data.canopy_size,
          canopy_serial_number: data.canopy_serial_number,
          equipment_tag: data.equipment_tag,
          visual_checks: data.visual_checks,
          physical_checks: data.physical_checks,
          corrective_actions: data.corrective_actions,
          canopy_found_okay: data.canopy_found_okay,
          summary_remarks: data.summary_remarks,
          other_comments: data.other_comments,
          comments: data.comments,
          manager_approve: data.manager_approve,
          punch_points: data.punch_points,
        },
      };
      dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    }, 2000),
    [dispatch, formId]
  );

  useEffect(() => {
    autoSave(formValues);
  }, [formValues, autoSave]);

  // ---------- Load existing ----------
  useEffect(() => {
    if (formId) {
      dispatch({ type: VIEW_FORM, payload: { formId, formName: 'canopy' } });
    }
    setIsLoading(false);
  }, [dispatch, formId]);

  useEffect(() => {
    if (viewFormData) {
      const d = viewFormData || {};
      setValue('stationId', d.stationId || d.station_id || stationId || '');
      setValue('station_id', d.station_id || d.stationId || stationId || '');
      setValue('project_name', d.project_name || '');
      setValue('inspection_date', d.inspection_date || '');
      setValue('vendor_name', d.vendor_name || '');
      setValue('canopy_size', d.canopy_size || '');
      setValue('canopy_serial_number', d.canopy_serial_number || '');
      setValue('equipment_tag', d.equipment_tag || '');

      setValue('visual_checks', Array.isArray(d.visual_checks) ? d.visual_checks : Array(32).fill({ status: '', remark: '' }));
      setValue('physical_checks', Array.isArray(d.physical_checks) ? d.physical_checks : Array(7).fill({ status: '', remark: '' }));
      setValue('corrective_actions', Array.isArray(d.corrective_actions) ? d.corrective_actions : Array(5).fill({ issue: '', action: '', target_date: '' }));

      setValue('canopy_found_okay', d.canopy_found_okay || '');
      setValue('summary_remarks', d.summary_remarks || '');
      setValue('other_comments', d.other_comments || '');
      setValue('comments', d.comments || {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      });
      setValue('status', d.status || 'draft');
      setValue('manager_approve', !!d.manager_approve);
      setValue('manager_id', d.l1?.managerId || d.managerId || null);
      setValue('punch_points', viewFormData?.punch_points || '');
      dataLoadedRef.current = true;
    }
  }, [viewFormData, stationId, setValue]);

  // ---------- Helpers ----------
  const status = () => getValues('status');

  const fieldsEditable =
    ((!manager_id || manager_id === userId) && ((role === 'engineer' && (statusWatch === 'draft' || statusWatch === 'CONTRACTOR' || statusWatch === 'MANAGER')) || (role === 'contractor' && (statusWatch === 'draft' || statusWatch === 'CONTRACTOR'))));

  const canComment =
    (role === 'engineer' && (status() === 'draft' || status() === 'CONTRACTOR')) ||
    (role === 'contractor' && status() === 'CONTRACTOR') ||
    (role === 'manager' && status() === 'MANAGER') ||
    (role === 'quality' && status() === 'QUALITY');

  const isSigEditable = (sigRole) => {
    if (role !== 'quality' && manager_id && manager_id !== userId) return false;
    return (
      (role === 'engineer' && sigRole === 'charge_engineer' && (status() === 'draft' || status() === 'CONTRACTOR' || status() === 'MANAGER')) ||
      (role === 'contractor' && sigRole === 'contractor' && (status() === 'draft' || status() === 'CONTRACTOR')) ||
      (role === 'quality' && sigRole === 'quality' && status() === 'QUALITY')
    );
  };

  // ---------- Signature Handlers ----------
  const handleImageUpload = (e, sigRole) => {
    const file = e.target.files[0];
    const maxSizeInBytes = 0.05 * 1024 * 1024; // 50KB
    if (file) {
      if (file.size > maxSizeInBytes) {
        alert('File size exceeds 50kb. Please upload a smaller image.');
        handleResetSignature(sigRole);
        sigCanvas.current?.clear();
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setValue(`comments.${sigRole}.signature`, reader.result, { shouldDirty: true, shouldValidate: true });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureTypeChange = (sigRole, type) => {
    setValue(`comments.${sigRole}.signatureType`, type, { shouldDirty: true, shouldValidate: true });
    setValue(`comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    if (type === 'digital') {
      setCurrentSignature({ role: sigRole });
      setShowModal(true);
    }
  };

  const handleResetSignature = (sigRole) => {
    setValue(`comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    setValue(`comments.${sigRole}.signatureType`, 'upload', { shouldDirty: true, shouldValidate: true });
  };

  const handleSaveSignature = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL();
      setValue(`comments.${currentSignature.role}.signature`, signatureData, { shouldDirty: true, shouldValidate: true });
    }
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  const handleClearSignature = () => {
    sigCanvas.current?.clear();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  // ---------- Submit ----------
  const onSubmit = (action) => (data) => {
    let newStatus = data.status;
    let manager_approve = data.manager_approve;

    const userRole = (role || cookie.get('user-role') || '').toLowerCase();

    if (userRole === 'engineer' && status() !== 'draft' && status() !== 'CONTRACTOR') return;
    if (userRole === 'contractor' && status() !== 'draft' && status() !== 'CONTRACTOR') return;

    if (userRole === 'engineer') {
      newStatus = 'CONTRACTOR';
    } else if (userRole === 'contractor') {
      newStatus = 'MANAGER';
    } else if (userRole === 'manager') {
      if (action === 'approve') {
        newStatus = 'QUALITY';
        manager_approve = true;
      } else if (action === 'rework' || action === 'draft') {
        newStatus = 'draft';
      } else {
        return;
      }
    } else if (userRole === 'quality') {
      if (action === 'approve') {
        newStatus = 'approved';
      } else if (action === 'rework' || action === 'draft') {
        newStatus = 'draft';
      } else {
        return;
      }
    } else {
      return;
    }

    setValue('status', newStatus);
    autoSave.cancel();

    const payload = {
      form_data: {
        status: newStatus,
        form_id: formId,
        formName: 'canopy',
        station_id: data.station_id || data.stationId,
        stationId: data.stationId || data.station_id,
        project_name: data.project_name,
        inspection_date: data.inspection_date,
        vendor_name: data.vendor_name,
        canopy_size: data.canopy_size,
        canopy_serial_number: data.canopy_serial_number,
        equipment_tag: data.equipment_tag,
        visual_checks: data.visual_checks,
        physical_checks: data.physical_checks,
        corrective_actions: data.corrective_actions,
        canopy_found_okay: data.canopy_found_okay,
        summary_remarks: data.summary_remarks,
        other_comments: data.other_comments,
        comments: data.comments,
        manager_approve,
        punch_points: data.punch_points,
      },
    };

    dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });

    const msg =
      role === 'manager' || role === 'quality'
        ? action === 'approve' ? 'Submitted' : 'Saved as Draft'
        : (action === 'pending' ? 'Submitted' : 'Saved as Draft');

    toast.success(`${msg} Successfully`);
    navigate(-1);
  };

  const renderCheckRow = (desc, index, prefix = 'visual_checks') => (
    <tr key={index}>
      <td>{index + 1}</td>
      <td>{desc}</td>
      <td>
        {['Yes', 'No', 'NA'].map((v) => (
          <Form.Check
            inline
            key={v}
            label={v}
            type="radio"
            name={`${prefix}_${index}`}
            value={v}
            {...register(`${prefix}.${index}.status`)}
            disabled={!fieldsEditable}
          />
        ))}
      </td>
      <td>
        <Form.Control type="text" {...register(`${prefix}.${index}.remark`)} disabled={!fieldsEditable} />
      </td>
    </tr>
  );

  const visualCheckItems = [
    'Is the baseplate free from rust, corrosion, or cracks?',
    'Is the J-bolt cap in place and properly secured?',
    'Are there any cracks, bends, or deformation in the frame?',
    'Are all welded joints and connections intact?',
    'Is there any rust, corrosion, or paint chipping on the frame?',
    'Are there any tears, holes, or sagging in the fabric or roofing material?',
    'Is there any discoloration, UV damage, or aging?',
    'Is there any shifting, leaning, or instability in the structure?',
    'Are the ACP sheets free from dents or peeling?',
    'Are there any scratches, wear, or damage on the ACP sheets?',
    'Are the ACP sheet joints properly sealed?',
    'Is there any water pooling on the canopy surface?',
    'Are drainage channels or gutters free from blockages?',
    'Are the downspouts unblocked?',
    'Are lighting fixtures free from damage or exposed wiring?',
    'Is cable management neat and secure?',
    'Is the waterproofing of electrical fixtures intact?',
    'Is the CCTV installed at the correct location as per the drawing?',
    'Is the CCTV power cable routed through a conduit?',
    'Is the CCTV LAN cable routed through a conduit?',
    'Is the router positioned and fixed according to the drawing?',
    'Is the area free from dirt, stains, or oil residues?',
    'Is the surface free from graffiti, stickers, or unwanted markings?',
    'Are all signs, logos, and branding intact?',
    'Is the signage legible and clearly visible?',
    'Is the local language branding correctly placed?',
    'Are the information hoardings in good condition?',
    'Are the bollards and car stoppers properly aligned?',
    'Are there any cracks, rust, or impact damage visible?',
    'Is there any soil erosion, uneven ground, or standing water?',
    'Is proper drainage maintained around the canopy?',
    'Are the surrounding areas free of obstructions or safety hazards?',
  ];

  const physicalCheckItems = [
    'Are there any loose or missing bolts and fasteners?',
    'Are all bolts, nuts, and fasteners properly tightened?',
    'Is the roofing material properly installed?',
    'Is there any shifting, leaning, or instability in the structure?',
    'Are the waterproofing materials intact?',
    'Is the waterproofing of electrical fixtures intact?',
    'Are the foundations of bollards and car stoppers secure?',
  ];

  const canEditPunchPoints = ['manager', 'quality', 'maintenance', 'operation'].includes((role || '').toLowerCase());
  const handleReworkClick = (levelOrAction, actionStr) => (e) => {
    if (e) e.preventDefault();
    if (actionStr) {
      setPendingRework({ level: levelOrAction, action: actionStr });
    } else {
      setPendingRework({ action: levelOrAction });
    }
    setShowPunchModal(true);
  };

  const handleSavePunchPoints = async () => {
    if (pendingRework) {
      setShowPunchModal(false);
      const currentValues = getValues();
      if (pendingRework.level) {
        onSubmit(pendingRework.level, pendingRework.action)(currentValues);
      } else {
        onSubmit(pendingRework.action)(currentValues);
      }
    }
  };

  if (isLoading) return <div>Loading...</div>;

  const renderSignatureCell = (sigRole, field) => {
    const editable = isSigEditable(sigRole);

    if (field === 'Date') {
      return <Form.Control type="date" {...register(`comments.${sigRole}.date`)} disabled={!editable} />;
    }
    if (field === 'Signature') {
      return (
        <div>
          <Form.Select
            {...register(`comments.${sigRole}.signatureType`)}
            onChange={(e) => handleSignatureTypeChange(sigRole, e.target.value)}
            className="mb-2"
            disabled={!editable}
          >
            <option value="upload">Image Upload</option>
            <option value="digital">Digital Sign</option>
          </Form.Select>

          {_.get(formValues, `comments.${sigRole}.signatureType`) === 'upload' && (
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, sigRole)}
              className="mb-2"
              disabled={!editable}
            />
          )}

          {_.get(formValues, `comments.${sigRole}.signature`) && (
            <div>
              <img
                src={_.get(formValues, `comments.${sigRole}.signature`)}
                alt="Signature"
                style={{ maxWidth: '100px', marginTop: '5px' }}
              />
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleResetSignature(sigRole)}
                className="mt-2"
                disabled={!editable}
              >
                Reset Signature
              </Button>
            </div>
          )}
        </div>
      );
    }
    return (
      <Form.Control
        type="text"
        {...register(`comments.${sigRole}.${field.toLowerCase()}`)}
        disabled={!editable}
      />
    );
  };

  return (
    <div className="page-body container-xl">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="page-title">Canopy - Site Acceptance Testing</h2>
        <Button variant="outline-warning" size="sm" onClick={() => setShowPunchModal(true)}>
          💬 See Comments
        </Button>
      </div>

      {role === 'engineer' && status() !== 'draft' && (
        <div className="alert alert-warning mb-4">This form is in View Mode Only for Engineer.</div>
      )}

      <Form className="form-content-pane">
        {punch_points && (
          <Alert variant="warning" className="d-flex align-items-center justify-content-between shadow-sm border-0 mb-4" style={{ borderRadius: '12px', background: '#fff9db', color: '#856404' }}>
            <div className="d-flex align-items-center">
              <IoInformationCircleOutline className="me-2 text-warning" size={24} />
              <div>
                <strong className="d-block">Rework Comments Available</strong>
                <span className="small">Please review the comments from the manager/quality team.</span>
              </div>
            </div>
            <Button 
              variant="warning" 
              size="sm" 
              className="rounded-pill px-3 fw-bold shadow-sm"
              onClick={() => setShowPunchModal(true)}
            >
              View Comments
            </Button>
          </Alert>
        )}

        <h3>1. Equipment Details</h3>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Station ID</Form.Label>
              <Form.Control type="text" {...register('station_id')} readOnly />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Project Name</Form.Label>
              <Form.Control type="text" {...register('project_name')} disabled={!fieldsEditable} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Inspection Date</Form.Label>
              <Form.Control type="date" {...register('inspection_date')} disabled={!fieldsEditable} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Vendor Name</Form.Label>
              <Form.Control type="text" {...register('vendor_name')} disabled={!fieldsEditable} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Canopy Size</Form.Label>
              <Form.Control type="text" {...register('canopy_size')} disabled={!fieldsEditable} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Canopy Serial Number</Form.Label>
              <Form.Control type="text" {...register('canopy_serial_number')} disabled={!fieldsEditable} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Equipment Tag</Form.Label>
              <Form.Control type="text" {...register('equipment_tag')} disabled={!fieldsEditable} />
            </Form.Group>
          </Col>
        </Row>

        <h3 className="mt-4">2. Visual Checks</h3>
        <Table bordered responsive>
          <thead>
            <tr>
              <th>Sr. No.</th>
              <th>Description</th>
              <th>Status</th>
              <th>Remark</th>
            </tr>
          </thead>
          <tbody>{visualCheckItems.map((item, i) => renderCheckRow(item, i, 'visual_checks'))}</tbody>
        </Table>

        <h3 className="mt-4">3. Physical Checks</h3>
        <Table bordered responsive>
          <thead>
            <tr>
              <th>Sr. No.</th>
              <th>Description</th>
              <th>Status</th>
              <th>Remark</th>
            </tr>
          </thead>
          <tbody>{physicalCheckItems.map((item, i) => renderCheckRow(item, i, 'physical_checks'))}</tbody>
        </Table>

        <h3 className="mt-4">Corrective and Preventive Action Table</h3>
        <Table bordered responsive>
          <thead>
            <tr>
              <th>Sr. No.</th>
              <th>Identified Issue</th>
              <th>Corrective Action</th>
              <th>Target Completion Date</th>
            </tr>
          </thead>
          <tbody>
            {Array(5).fill(0).map((_, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td><Form.Control type="text" {...register(`corrective_actions.${i}.issue`)} disabled={!fieldsEditable} /></td>
                <td><Form.Control type="text" {...register(`corrective_actions.${i}.action`)} disabled={!fieldsEditable} /></td>
                <td><Form.Control type="date" {...register(`corrective_actions.${i}.target_date`)} disabled={!fieldsEditable} /></td>
              </tr>
            ))}
          </tbody>
        </Table>

        <h3 className="mt-4">Inspection Summary</h3>
        <Form.Group className="mb-3">
          <Form.Label>Canopy Found Okay</Form.Label>
          <div>
            {['Yes', 'No'].map((v) => (
              <Form.Check inline key={v} label={v} type="radio" value={v} {...register('canopy_found_okay')} disabled={!fieldsEditable} />
            ))}
          </div>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Remarks</Form.Label>
          <Form.Control as="textarea" rows={3} {...register('summary_remarks')} disabled={!fieldsEditable} />
        </Form.Group>

        <h3 className="mt-4">4. Other Comments / Remarks</h3>
        <Form.Group className="mb-3">
          <Form.Control as="textarea" rows={4} {...register('other_comments')} disabled={!canComment} />
        </Form.Group>

        <h3 className="mt-4">5. Comments & Signatures</h3>
        <Table bordered responsive>
          <thead>
            <tr>
              <th></th>
              <th>Engineer - Contractor</th>
              <th>Engineer - Charge Zone</th>
              <th>Quality - Charge Zone</th>
              <th>Optional</th>
            </tr>
          </thead>
          <tbody>
            {['Name', 'Company', 'Signature', 'Date'].map((field) => (
              <tr key={field}>
                <td>{field}</td>
                {['contractor', 'charge_engineer', 'quality', 'optional'].map((sigRole) => (
                  <td key={sigRole}>{renderSignatureCell(sigRole, field)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>

        {role === 'manager' && statusWatch === 'MANAGER' ? (
          <div className="mt-4">
            <Button variant="success" onClick={handleSubmit(onSubmit('approve'))} className="me-2">
              Approve
            </Button>
            <Button variant="danger" onClick={handleReworkClick('draft')}>
              Re-Work
            </Button>
          </div>
        ) : role === 'quality' && statusWatch === 'QUALITY' ? (
          <div className="mt-4">
            <Button variant="success" onClick={handleSubmit(onSubmit('approve'))} className="me-2">
              Approve
            </Button>
            <Button variant="danger" onClick={handleReworkClick('draft')}>
              Apply for Resubmit
            </Button>
          </div>
        ) : (
          <div className="mt-4">
            <Button
              variant="success"
              onClick={handleSubmit(onSubmit('pending'))}
              disabled={
                role === 'engineer' ? (statusWatch !== 'draft' && statusWatch !== 'CONTRACTOR')
                : role === 'contractor' ? statusWatch !== 'CONTRACTOR'
                : true
              }
            >
              Submit
            </Button>
            <Button
              variant="secondary"
              style={{ marginLeft: '10px' }}
              onClick={handleReworkClick('draft')}
              disabled={
                role === 'engineer' ? (statusWatch !== 'draft' && statusWatch !== 'CONTRACTOR')
                : role === 'contractor' ? statusWatch !== 'CONTRACTOR'
                : true
              }
            >
              Save As Draft
            </Button>
          </div>
        )}
      </Form>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Draw Signature</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SignatureCanvas
            ref={sigCanvas}
            penColor="black"
            canvasProps={{ width: 500, height: 200, className: 'sigCanvas border' }}
          />
          <div className="mt-2">
            <Button variant="secondary" onClick={handleClearSignature} className="me-2">
              Clear
            </Button>
            <Button variant="primary" onClick={handleSaveSignature}>
              Save Signature
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <PunchPointsModal
        show={showPunchModal}
        onHide={() => { setShowPunchModal(false); setPendingRework(null); }}
        value={punch_points}
        onChange={(val) => setValue('punch_points', val)}
        onSave={handleSavePunchPoints}
        canEdit={canEditPunchPoints && !!pendingRework}
      />
    </div>
  );
};

CanopyForm.propTypes = {
  role: PropTypes.oneOf(['engineer', 'contractor', 'manager', 'quality', 'admin']),
  userId: PropTypes.string,
};

export default CanopyForm;
